import {
  signOut as amplifySignOut,
  AuthError,
  fetchAuthSession,
  getCurrentUser,
} from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import * as Sentry from '@sentry/react';

export enum AuthErrorType {
  USER_ALREADY_AUTHENTICATED = 'UserAlreadyAuthenticatedException',
  USER_NOT_FOUND = 'UserNotFoundException',
  INCORRECT_USERNAME_PASSWORD = 'NotAuthorizedException',
  USER_NOT_CONFIRMED = 'UserNotConfirmedException',
  TOO_MANY_ATTEMPTS = 'TooManyRequestsException',
  INVALID_PASSWORD = 'InvalidPasswordException',
  USER_TEMPORARILY_LOCKED = 'UserTemporarilyLockedException',
  PASSWORD_RESET_REQUIRED = 'PasswordResetRequiredException',
  NETWORK_ERROR = 'NetworkError',
  UNKNOWN = 'UnknownError',
}

export interface AuthErrorInfo {
  type: AuthErrorType;
  message: string;
  userFriendlyMessage: string;
  canRetry: boolean;
  suggestedAction?: string;
}

/**
 * Enhanced sign-out function with comprehensive session cleanup
 */
export const enhancedSignOut = async (): Promise<void> => {
  try {
    // Step 1: Sign out from Amplify (this handles most of the cleanup)
    await amplifySignOut({ global: true });

    // Step 2: Clear any remaining local storage items
    const authKeys = ['amplify-authenticator-authState', 'CognitoIdentityServiceProvider'];

    // Clear localStorage items that might contain auth state
    authKeys.forEach(key => {
      Object.keys(localStorage).forEach(storageKey => {
        if (storageKey.includes(key)) {
          localStorage.removeItem(storageKey);
        }
      });
    });

    // Step 3: Clear sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      if (key.includes('cognito') || key.includes('amplify') || key.includes('auth')) {
        sessionStorage.removeItem(key);
      }
    });

    // Step 4: Clear any cached credentials
    if ((window as any).AWS?.config?.credentials) {
      (window as any).AWS.config.credentials.clearCachedId?.();
    }

    // Step 5: Emit custom event for any components that need to react to logout
    Hub.dispatch('auth', {
      event: 'enhancedSignOut',
      data: { timestamp: Date.now() },
    });
  } catch (error) {
    // Even if there's an error, try to clear what we can locally
    localStorage.clear();
    sessionStorage.clear();
    throw error;
  }
};

/**
 * Check if a user is currently authenticated
 */
export const checkAuthState = async (): Promise<{ isAuthenticated: boolean; user?: any }> => {
  try {
    const user = await getCurrentUser();

    return { isAuthenticated: true, user };
  } catch (error) {
    return { isAuthenticated: false };
  }
};

/**
 * Parse and categorize authentication errors for better user experience
 */
export const parseAuthError = (error: any): AuthErrorInfo => {
  let errorType = AuthErrorType.UNKNOWN;
  let userFriendlyMessage = 'An unexpected error occurred. Please try again.';
  let canRetry = true;
  let suggestedAction: string | undefined;

  const errorMessage = error?.message || error?.toString() || '';
  const errorCode = error?.name || error?.code || '';

  // Handle specific Cognito errors
  if (error instanceof AuthError || errorCode) {
    switch (errorCode) {
      case 'UserAlreadyAuthenticatedException':
        errorType = AuthErrorType.USER_ALREADY_AUTHENTICATED;
        userFriendlyMessage =
          'You are already signed in. Please refresh the page or sign out first.';
        suggestedAction =
          'Try refreshing the page or signing out completely before signing in again.';
        canRetry = false;
        break;
      case 'UserNotFoundException':
        errorType = AuthErrorType.USER_NOT_FOUND;
        userFriendlyMessage = 'No account found with this email address.';
        suggestedAction = 'Please check your email address or create a new account.';
        break;
      case 'NotAuthorizedException':
        errorType = AuthErrorType.INCORRECT_USERNAME_PASSWORD;

        if (errorMessage.includes('password')) {
          userFriendlyMessage = 'Incorrect password. Please try again.';
        } else {
          userFriendlyMessage = 'Incorrect email or password. Please try again.';
        }

        suggestedAction = 'Double-check your credentials or use "Forgot Password" if needed.';
        break;
      case 'UserNotConfirmedException':
        errorType = AuthErrorType.USER_NOT_CONFIRMED;
        userFriendlyMessage =
          'Your account is not confirmed. Please check your email for verification instructions.';
        suggestedAction = 'Check your email for a confirmation link or request a new one.';
        break;
      case 'TooManyRequestsException':
        errorType = AuthErrorType.TOO_MANY_ATTEMPTS;
        userFriendlyMessage =
          'Too many login attempts. Please wait a few minutes before trying again.';
        suggestedAction = 'Wait 15-30 minutes before attempting to sign in again.';
        canRetry = false;
        break;
      case 'InvalidPasswordException':
        errorType = AuthErrorType.INVALID_PASSWORD;
        userFriendlyMessage = 'Password does not meet security requirements.';
        suggestedAction = 'Ensure your password meets the minimum requirements.';
        break;
      case 'UserTemporarilyLockedException':
        errorType = AuthErrorType.USER_TEMPORARILY_LOCKED;
        userFriendlyMessage =
          'Your account has been temporarily locked due to multiple failed attempts.';
        suggestedAction = 'Wait 30 minutes or contact support for assistance.';
        canRetry = false;
        break;
      case 'PasswordResetRequiredException':
        errorType = AuthErrorType.PASSWORD_RESET_REQUIRED;
        userFriendlyMessage = 'You must reset your password before signing in.';
        suggestedAction = 'Use the "Forgot Password" link to reset your password.';
        canRetry = false;
        break;
    }
  }

  // Handle network errors
  if (
    errorMessage.includes('Network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('connection')
  ) {
    errorType = AuthErrorType.NETWORK_ERROR;
    userFriendlyMessage = 'Network connection error. Please check your internet connection.';
    suggestedAction = 'Check your internet connection and try again.';
  }

  // If we didn't classify the error (e.g. MFA `CodeMismatchException` wrapped
  // by `mfaErrorMessage` into a plain Error), prefer the error's own message
  // over the generic fallback so the user sees something actionable.
  if (errorType === AuthErrorType.UNKNOWN && errorMessage) {
    userFriendlyMessage = errorMessage;
  }

  return {
    type: errorType,
    message: errorMessage,
    userFriendlyMessage,
    canRetry,
    suggestedAction,
  };
};

/**
 * Safe authentication wrapper that handles the "already signed in" scenario
 */
export const safeAuthenticate = async (authFunction: () => Promise<any>) => {
  try {
    // First check if user is already authenticated
    const { isAuthenticated } = await checkAuthState();

    if (isAuthenticated) {
      // If already authenticated, force a clean sign-out first
      await enhancedSignOut();

      // Wait a bit for the sign-out to propagate
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Now attempt the authentication
    return await authFunction();
  } catch (error) {
    const parsedError = parseAuthError(error);

    // If it's still a "user already authenticated" error, try one more time with forced cleanup
    if (parsedError.type === AuthErrorType.USER_ALREADY_AUTHENTICATED) {
      // Force clear everything
      localStorage.clear();
      sessionStorage.clear();

      // Try to reload the page to completely reset the state
      window.location.reload();

      return;
    }

    throw parsedError;
  }
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Returns true when the error from fetchAuthSession is definitively
 * non-recoverable — i.e., retrying will never help because the refresh
 * token itself is invalid, revoked, or the user account is gone.
 *
 * We intentionally keep this list narrow: any error we DON'T recognize
 * is treated as transient and retried, so we err on the side of keeping
 * users logged in rather than kicking them out.
 */
const isNonRecoverableAuthError = (err: unknown): boolean => {
  const name = (err as { name?: string })?.name || '';
  const message = ((err as { message?: string })?.message || '').toLowerCase();

  // Refresh token was explicitly revoked (admin action / password change)
  if (message.includes('refresh token has been revoked')) return true;
  if (message.includes('refresh token has expired')) return true;

  // Cognito identity pool mismatch — cached identity credentials are stale
  if (message.includes("logins don't match")) return true;

  // Cognito says the credentials are flat-out wrong / user is gone
  if (name === AuthErrorType.INCORRECT_USERNAME_PASSWORD) return true;
  if (name === AuthErrorType.USER_NOT_FOUND) return true;
  if (name === AuthErrorType.USER_NOT_CONFIRMED) return true;

  return false;
};

/**
 * Revalidate authentication after Amplify emits a `tokenRefresh_failure`
 * Hub event. Uses exponential back-off so transient issues (brief network
 * drops, Cognito throttling, DNS hiccups) don't log the user out.
 *
 * Returns `true` when session is recovered, `false` when the user should
 * be signed out.
 */
export const recoverFromTokenRefreshFailure = async (): Promise<boolean> => {
  const MAX_ATTEMPTS = 4;
  const BASE_DELAY_MS = 1000;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      await fetchAuthSession({ forceRefresh: true });

      if (attempt > 0) {
        Sentry.addBreadcrumb({
          category: 'auth',
          message: `Token refresh recovered on attempt ${attempt + 1}`,
          level: 'info',
        });
      }

      return true;
    } catch (err) {
      const errName = (err as { name?: string })?.name || '';
      const errMsg = (err as { message?: string })?.message || '';

      if (isNonRecoverableAuthError(err)) {
        Sentry.addBreadcrumb({
          category: 'auth',
          message: `Token refresh non-recoverable: ${errName} – ${errMsg}`,
          level: 'warning',
        });

        return false;
      }

      Sentry.addBreadcrumb({
        category: 'auth',
        message: `Token refresh attempt ${attempt + 1}/${MAX_ATTEMPTS} failed (transient): ${errName} – ${errMsg}`,
        level: 'info',
      });

      if (attempt < MAX_ATTEMPTS - 1) {
        await sleep(BASE_DELAY_MS * Math.pow(2, attempt) * (0.5 + Math.random() * 0.5));
      }
    }
  }

  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Token refresh recovery exhausted all retries',
    level: 'warning',
  });

  return false;
};

// Export for dev/debugging purposes
if (import.meta.env.DEV) {
  (window as any).enhancedSignOut = enhancedSignOut;
}
